'use client';
import React, { ReactElement } from 'react';
import { FAQ } from '@benzinga/ui';
import { Schema } from '@benzinga/seo';
import ReactDOMServer from 'react-dom/server';
import { decode } from 'html-entities';

interface FAQInterface {
  answer: React.ReactNode | string;
  question: string;
}

interface FAQSchemaAnswer {
  '@type': string;
  text: string;
}

interface FAQSchemaQuestion {
  '@type': string;
  acceptedAnswer: FAQSchemaAnswer;
  name: string;
}

type FAQSchema = {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: FAQSchemaQuestion[];
} & Record<string, unknown>;

interface FAQProps {
  FAQsDataSet: FAQInterface[];
  title?: string;
}

const extractTextFromHTML = (html: string): string => {
  if (!html) {
    return '';
  }

  let text = html.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
  text = text.replace(/<[^>]+>/g, '');
  text = decode(text, { level: 'html5' });
  text = text.replace(/\s+/g, ' ').trim();

  return text;
};

const getFAQSchemaText = (answer: React.ReactNode | string): string => {
  try {
    if (typeof answer === 'string') {
      return extractTextFromHTML(answer);
    }

    const htmlString = ReactDOMServer.renderToStaticMarkup(answer as ReactElement);
    return extractTextFromHTML(htmlString);
  } catch (error) {
    console.error('Error rendering FAQ answer to HTML:', error);
    return answer as string;
  }
};

export const FAQs: React.FC<FAQProps> = ({ FAQsDataSet, title }) => {
  const formatSchemaData = (FAQData: FAQInterface[]): FAQSchema => {
    const mappedQuestions = FAQData.map(item => {
      return {
        '@type': 'Question',
        acceptedAnswer: {
          '@type': 'Answer',
          text: getFAQSchemaText(item.answer),
        },
        name: item.question,
      };
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: mappedQuestions,
    };
  };

  return (
    <div className="mt-6">
      <Schema data={formatSchemaData(FAQsDataSet)} name="faq" />
      <FAQ faqData={FAQsDataSet} title={title} />
    </div>
  );
};

export default FAQs;
